import React, { useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { styled } from "@mui/system";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./SignUpForm.css";
import { Slide, ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { phone } from "phone";
import lawgptLogo from "./../assets/images/lawgptLogo.png";
import backgroundImage from "./../assets/images/bgImage.png";
import { BACKEND_ENDPOINTS } from "../services/apiEndpoints";

const emptyUser = { fullName: "", email: "", contact: "" };

const StyledImage = styled("img")(({ theme }) => ({
  maxWidth: "70px",
  height: "auto",
  objectFit: "contain",
  [theme.breakpoints.down("md")]: {
    maxWidth: "56px",
  },
}));

export default function NadraOnboardingForm() {
  const [countryCodes, setCountryCodes] = useState(["pk"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePhoneNumber = (inputNumber, index) => {
    if (!inputNumber) return "Contact number is required";

    const { isValid } = phone(inputNumber, {
      country: countryCodes[index] || "pk",
    });
    return isValid ? true : "Invalid phone number format";
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: { users: [emptyUser] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "users" });

  const handleAddUser = async () => {
    const isValid = await trigger("users");
    if (!isValid) {
      toast.error("Please fill in all required fields before adding another user.", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Slide,
      });
      return;
    }
    append(emptyUser);
    setCountryCodes((prev) => [...prev, "pk"]);
  };

  const handleRemoveUser = (index) => {
    remove(index);
    setCountryCodes((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    const payload = data.users.map(({ fullName, email, contact }) => ({
      name: fullName,
      email: email?.toLowerCase(),
      contact,
    }));

    setIsSubmitting(true);
    try {
      const { data: response } = await axios.post(
        `${BACKEND_ENDPOINTS.BASE_URL}${BACKEND_ENDPOINTS.ADD_NADRA_USERS}`,
        payload
      );
      toast.success(response.message || "Submitted successfully.", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Slide,
      });
      reset();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Something went wrong. Please try again.",
        {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Slide,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>LawGPT - Onboarding &amp; Verification</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          width: "100vw",
          minHeight: "100vh",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: { xs: "90%", sm: "80%", md: "60%", lg: "45%", xl: "34%" },
            margin: "20px",
            boxSizing: "border-box",
            padding: { xs: "1.5rem", md: "2rem" },
            borderRadius: "20px",
            backgroundColor: "rgba(240, 242, 245, 0.97)",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              mb: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <StyledImage src={lawgptLogo} alt="LawGPT Logo" />
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: { xs: "22px", sm: "26px", md: "30px" },
              fontWeight: 700,
              color: "#003D78",
              lineHeight: 1.25,
              mb: 1.5,
            }}
          >
            Submission: Onboarding &amp; Verification Information Request Form
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: { xs: "14px", sm: "16px" },
              color: "#3d3d3d",
              mb: 3,
              ml: 5,
              mr: 5
            }}
          >
            Welcome to LawGPT - Add your details to create your user profile.
          </Typography>

          {fields.map((fieldItem, index) => (
            <Box key={fieldItem.id} sx={{ width: "100%" }}>
              {index > 0 && (
                <>
                  <Divider sx={{ width: "100%", my: 2.5 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#003D78",
                      }}
                    >
                      User {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveUser(index)}
                      sx={{ color: "#B3261E" }}
                      aria-label="Remove user"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              )}
              <Grid container spacing={2} sx={{ width: "100%" }}>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    label="Full Name"
                    {...register(`users.${index}.fullName`, {
                      required: "Full Name is required",
                    })}
                    error={!!errors.users?.[index]?.fullName}
                    helperText={errors.users?.[index]?.fullName?.message}
                    FormHelperTextProps={{
                      sx: { fontSize: "10px", marginTop: "2px" },
                    }}
                    InputProps={{
                      sx: {
                        bgcolor: "white",
                        borderRadius: "8px",
                        fontSize: { xs: "12px", sm: "14px" },
                        "& input:-webkit-autofill": {
                          backgroundColor: "white !important",
                          WebkitBoxShadow: "0 0 0 30px white inset !important",
                          WebkitTextFillColor: "black !important",
                        },
                      },
                    }}
                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Email"
                    variant="outlined"
                    type="email"
                    {...register(`users.${index}.email`, {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address",
                      },
                    })}
                    error={!!errors.users?.[index]?.email}
                    helperText={errors.users?.[index]?.email?.message}
                    FormHelperTextProps={{
                      sx: { fontSize: "10px", marginTop: "2px" },
                    }}
                    InputProps={{
                      sx: {
                        bgcolor: "white",
                        borderRadius: "8px",
                        fontSize: { xs: "12px", sm: "14px" },
                        "& input:-webkit-autofill": {
                          backgroundColor: "white !important",
                          WebkitBoxShadow: "0 0 0 30px white inset !important",
                          WebkitTextFillColor: "black !important",
                        },
                      },
                    }}
                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={`users.${index}.contact`}
                    control={control}
                    rules={{
                      required: "Contact number is required",
                      validate: (value) => validatePhoneNumber(value, index),
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        {...field}
                        country={countryCodes[index] || "pk"}
                        disableCountryCode={false}
                        disableSearchIcon
                        enableSearch
                        placeholder="+92"
                        value={field.value}
                        onChange={(value, countryData) => {
                          field.onChange(value);
                          setCountryCodes((prev) => {
                            const next = [...prev];
                            next[index] = countryData.countryCode;
                            return next;
                          });
                        }}
                        inputProps={{ required: true }}
                        inputStyle={{
                          width: "100%",
                          height: "40px",
                          backgroundColor: "white",
                          borderColor: errors.users?.[index]?.contact
                            ? "red"
                            : "#ced4da",
                          borderRadius: "8px",
                          paddingLeft: "48px",
                        }}
                        buttonStyle={{
                          borderColor: errors.users?.[index]?.contact
                            ? "red"
                            : "#ced4da",
                          backgroundColor: "white",
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                        }}
                        containerStyle={{ width: "100%" }}
                      />
                    )}
                  />
                  {errors.users?.[index]?.contact && (
                    <Typography
                      color="error"
                      sx={{ fontSize: "10px", mt: "2px", ml: "14px" }}
                    >
                      {errors.users[index].contact.message}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}

          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddUser}
            sx={{
              alignSelf: { xs: "stretch", sm: "flex-start" },
              textTransform: "none",
              color: "#003D78",
              fontWeight: 600,
              mt: 2,
            }}
          >
            Add Another User
          </Button>

          <Button
            size="large"
            sx={{
              display: "flex",
              width: { xs: "100%", sm: "60%", md: "45%" },
              fontWeight: "bold",
              borderRadius: "10px",
              backgroundColor: "#003D78",
              marginTop: "16px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#002F60" },
              transition: "background-color 0.3s ease-in-out",
            }}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              `Submit${fields.length > 1 ? ` (${fields.length})` : ""}`
            )}
          </Button>
        </Box>
        <ToastContainer
          position="top-center"
          autoClose={2000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          transition="Slide"
        />
      </Box>
    </>
  );
}
